"use client"

import { BadgeInfo } from "lucide-react"
import { motion, MotionConfig } from "motion/react"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { FAQClient } from "./faq-client"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import { comprehensiveFaqData, newCategories } from "./faq-data-new"

import { JsonLd } from "@/components/seo/json-ld"
interface FAQItem {
  id: string
  question: string
  answer: string
  category: string
  order: number
}

// Static FAQ data for SEO fallback - now using comprehensive data
const staticFaqData: FAQItem[] = comprehensiveFaqData

const categories = newCategories

// Generate FAQ Schema for SEO
function generateFAQSchema(faqData: FAQItem[]) {
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://whatgrassisthis.com'
  
  const faqSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "FAQPage",
        "@id": `${siteUrl}/faq#faqpage`,
        "name": "Lawn Care Questions & Answers",
        "description": "Find answers to common lawn care questions about grass identification, watering, mowing, fertilizing, and more.",
        "url": `${siteUrl}/faq`,
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${siteUrl}#website`
        },
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      },
      {
        "@type": "WebPage",
        "@id": `${siteUrl}/faq#webpage`,
        "name": "FAQ - What Grass Is This? | Lawn Care Questions & Answers",
        "description": "Find answers to common lawn care questions about grass identification, watering, mowing, fertilizing, and more.",
        "url": `${siteUrl}/faq`,
        "inLanguage": "en-US",
        "isPartOf": {
          "@type": "WebSite",
          "@id": `${siteUrl}#website`
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": `${siteUrl}`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "FAQ",
              "item": `${siteUrl}/faq`
            }
          ]
        }
      }
    ]
  }
  
  return JSON.stringify(faqSchema)
}

export function FAQPageContent() {
  const faqData = staticFaqData

  // Using static comprehensive FAQ data instead of Directus
  // TODO: Migrate comprehensive FAQ data to Directus CMS
  // useEffect(() => {
  //   const fetchFAQs = async () => {
  //     try {
  //       const response = await fetch('/api/directus/faqs')
  //       if (!response.ok) throw new Error('Failed to fetch FAQs')
  //
  //       const data = await response.json()
  //       const mappedFaqs = data.faqs.map((faq: any) => ({
  //         id: faq.id,
  //         question: faq.question,
  //         answer: faq.answer,
  //         category: faq.category,
  //         order: faq.order
  //       }))
  //       setFaqData(mappedFaqs)
  //     } catch (error) {
  //       console.error('Error fetching FAQs:', error)
  //       // Fallback to static data if API fails
  //       setFaqData(staticFaqData)
  //     }
  //   }
  //
  //   fetchFAQs()
  // }, [])

  return (
    <MotionConfig reducedMotion="user">
    <ErrorBoundary>
      <div className="min-h-screen relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-linear-to-b from-green-50/40 via-transparent to-emerald-50/30" />
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[680px] w-[1200px] rounded-full bg-green-200/40 blur-[130px]" />
          <div className="absolute top-[40%] -right-48 h-[520px] w-[520px] rounded-full bg-emerald-200/30 blur-[130px]" />
          <div className="absolute bottom-24 -left-48 h-[500px] w-[500px] rounded-full bg-amber-100/35 blur-[130px]" />
        </div>

        {/* Aria-live region for search results updates */}
        <div
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
          id="faq-results-announcement"
        >
          {faqData.length > 0 && `Showing ${faqData.length} frequently asked questions`}
        </div>
        
        {/* FAQ Schema for SEO */}
        <JsonLd id="faq-schema" data={generateFAQSchema(faqData)} />
        
      <motion.div
        {...FADE_UP} transition={FADE_TRANSITION}
        className="relative"
      >
        <div className="max-w-6xl mx-auto px-4 pt-6 md:pt-8 md:px-6 lg:px-8">
          <div>
            <div className="mb-2">
              <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "FAQ" }]} />
            </div>
            <div className="py-5 md:py-8">
              <div className="ds-glass mb-4 inline-flex items-center gap-2 rounded-full border border-green-200/70 bg-green-50/55 px-4 py-2 text-xs font-bold uppercase text-green-700 backdrop-blur-sm">
                <BadgeInfo className="h-3.5 w-3.5" aria-hidden="true" />
                Help center
              </div>
              <h1 className="font-editorial max-w-3xl text-4xl font-extrabold leading-tight text-gray-900 md:text-5xl">
                Frequently asked{" "}
                <span className="relative isolate inline-block text-green-600">
                  <span className="relative z-10">questions</span>
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -z-10 rounded-full bg-green-200/70"
                    style={{ bottom: "0.08em", height: "0.28em" }}
                  />
                </span>
              </h1>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-gray-700 md:text-[17px]">
                {`${comprehensiveFaqData.length} answers about grass identification, the calculators, pricing, and everyday lawn care. Search or browse by topic.`}
              </p>
            </div>

            {/* sr-only Q/A duplicate removed 2026-05-04: doubled HTML weight
                (~862KB → ~430KB). FAQPage JSON-LD above (line 26-85) already
                encodes every Q/A for Google rich results, and the visible
                accordion in FAQClient renders questions in the DOM. */}
          </div>
        </div>

        <div>
          <FAQClient faqData={faqData} categories={categories} />
        </div>
      </motion.div>

      </div>
    </ErrorBoundary>
    </MotionConfig>
  )
}
